import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import _ from 'lodash';

import { MatSelectChange } from '@angular/material/select';

import { Maybe } from '../../common/utils/utils';
import { Inventory } from '../inventory.model';
import { InventoryValidationIssue, ValidationStatus } from '../../common/model/validation-status.model';
import { ValidationStatusHelper } from '../../common/helpers/validation-status.helper';

@Component({
    selector: 'adm4-inventory-header',
    template: `
    <ng-template #popContentInventoryIssues>
      <adm4-inventory-validation-tooltip-content [inventoryValidationStatus]='inventoryValidationStatus'>
      </adm4-inventory-validation-tooltip-content>
    </ng-template>
    <adm4-column-header styleClass="inventory-header {{inventoryBackgroundClassName}}">
      @if (currentInventoryKey) {
        <div class='inventory-header-heading'>
          <span class='inventory-validation-dropdown-wrapper'>
            <span class="inventory-validation-indicator">
              <adm4-validation-indicator
                [ngbTooltip]='popContentInventoryIssues' [disableTooltip]='isValidationValid' placement='bottom-left'
                [isDisplayed]="true"
                [isValid]='isValidationValid'
                [isError]="isValidationWithErrorOrWarning"
                [isWarning]="false"
                [isInfo]='isValidationWithInfo'
              ></adm4-validation-indicator>
            </span>
            <adm4-inventory-dropdown
              [currentInventoryKey]='currentInventoryKey'
            [inventories]='inventories'></adm4-inventory-dropdown>
          </span>
          <span class="flex-grow-1"></span>
          @if (diffViewEnabled) {
            <mat-form-field class="diff-selector" [class.diff-rightmost]="!diffMode"
              [ngbTooltip]="'Select an Inventory to compare to'">
              <mat-select placeholder="Compare to..."
                [value]="selectedDiffInventory" (selectionChange)="onDiffInventorySelectionChanged($event)">
                @for (i of inventories; track i.inventoryKey) {
                  <mat-option [value]="i.inventoryKey">{{i.inventoryKey | cropTenantFromKey}}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
          @if (diffViewEnabled && diffMode) {
            <button type="button" class="close-diff admn4-button-icon-white" [class.diff-rightmost]="diffMode" (click)="closeDiffMode()">
              <mat-icon class="size-16 color-screen-black">close</mat-icon>
            </button>
          }
          @if (isHelpCollapsed) {
            <adm4-toggle-collapse-button class="flex-center-xy"
              side='right'
              [isCollapsed]='isHelpCollapsed'
              (keydown.enter)='$event.stopPropagation()'
              (click)='onHelpExpanderClick()'
            ></adm4-toggle-collapse-button>
          }
        </div>
      }
    </adm4-column-header>
    `,
    styleUrls: ['./inventory-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InventoryHeaderComponent implements OnChanges {
  @Input() currentInventory?: Inventory;
  @Input() inventories: Inventory[];
  @Input() inventoryValidationStatus: ValidationStatus<InventoryValidationIssue> | null;
  @Input() inventoryBackgroundClassName: string;
  @Input() readOnly: boolean;
  @Input() isHelpCollapsed: boolean;
  @Output() expandHelp: EventEmitter<undefined> = new EventEmitter();

  @Input() diffViewEnabled: boolean = false;
  @Input() selectedDiffInventory: Maybe<string>;
  @Output() diffInventorySelectionChanged: EventEmitter<Maybe<string>> = new EventEmitter();

  public diffMode: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedDiffInventory) {
      this.diffMode = !!this.selectedDiffInventory;
    }
  }

  onDiffInventorySelectionChanged(change: MatSelectChange) {
    this.diffInventorySelectionChanged.emit(change.value);
  }

  closeDiffMode() {
    this.diffInventorySelectionChanged.emit(null);
  }

  onHelpExpanderClick(): void {
    this.expandHelp.emit();
  }

  get isValidationValid(): boolean {
    return ValidationStatusHelper.isValidationValid(this.inventoryValidationStatus);
  }

  get isValidationWithErrorOrWarning(): boolean {
    return ValidationStatusHelper.isValidationWithError(this.inventoryValidationStatus) ||
        ValidationStatusHelper.isValidationWithWarning(this.inventoryValidationStatus);
  }

  get isValidationWithInfo(): boolean {
    return ValidationStatusHelper.isValidationWithInfo(this.inventoryValidationStatus) && !this.isValidationWithErrorOrWarning;
  }

  get currentInventoryKey(): string | null {
    return _.isNil(this.currentInventory) ? null : this.currentInventory.inventoryKey;
  }
}
